import React, { useState, useEffect, useMemo } from 'react'
import {
  Modal, Button, Form, Spinner as BSSpinner, Alert,
  Table, Badge, Row, Col, Card, Collapse,
} from 'react-bootstrap'
import Swal from 'sweetalert2'
import {
  actualizarDatos,
  getDataList,
  addEquipments,
  removeEquipoFromOt,
  POST,
  crearDatos,
} from '../../api/common'
import { Modalcarga } from '../Modales/Modalcarga'
import Spinner from '../commons/Spinner'
import { Alertassw } from '../Alertas/Alertassw'
import EquipoManager from './CrearOt/EquipoManager'
import ListaEquiposConfirmacion from './CrearOt/ListaEquiposConfirmacion'
import ModalDuplicados from './CrearOt/ModalDuplicados'
import ModalResolverItems from './CrearOt/ModalResolverItems'
import ModalEditarSede from './CrearOt/ModalEditarSede'
import { PARENT } from '../../constants'
import Select from 'react-dropdown-select'

const estadoBadge = (estado) => ({
  'Aprobado': 'success',
  'Finalizado': 'primary',
  'No Finalizado': 'warning',
  'Entregado': 'secondary',
  'Cancelado': 'danger',
})[estado] || 'light'

export const EditarOt = ({ ot, show, onHide, onSaved }) => {
  // ── Datos básicos ─────────────────────────────────────────────────
  const [numeroOrden, setNumeroOrden] = useState('')
  const [observacion, setObservacion] = useState('')

  // ── Estado completo de la OT (cargado al abrir) ───────────────────
  const [otFull, setOtFull] = useState(null)
  const [loadingOt, setLoadingOt] = useState(false)
  const [savingDatos, setSavingDatos] = useState(false)
  const [savingEquipos, setSavingEquipos] = useState(false)

  // ── Lista de equipos — filtros y visibilidad ──────────────────────
  const [listaEquiposVisible, setListaEquiposVisible] = useState(false)
  const [filterItem, setFilterItem] = useState('')
  const [filterMarca, setFilterMarca] = useState('')
  const [filterSerie, setFilterSerie] = useState('')
  const [filterSede, setFilterSede] = useState('')
  const [filterConsecutivo, setFilterConsecutivo] = useState('')
  const [filterEstado, setFilterEstado] = useState('')
  const [sortAZ, setSortAZ] = useState(false)

  // ── Equipos pendientes de agregar ─────────────────────────────────
  const [pendingEquipos, setPendingEquipos] = useState([])
  const [listaSedes, setListaSedes] = useState([])
  const [sedeSelect, setSedeSelect] = useState(null)
  const [clienteData, setClienteData] = useState(null)
  const [modalCrearSede, setModalCrearSede] = useState({ show: false })

  // ── Selección múltiple para eliminar equipos ──────────────────────
  // Al eliminar antes se llamaba onSaved() tras cada equipo, lo que
  // cerraba el modal (el padre hace setModalEditarOt(false)) y obligaba
  // a reabrir para seguir borrando. Ahora se seleccionan por checkbox
  // y se eliminan en lote sin cerrar el modal; onSaved() se dispara una
  // sola vez al cerrar manualmente, si hubo cambios.
  const [seleccionadosEliminar, setSeleccionadosEliminar] = useState(new Set())
  const [eliminandoSeleccionados, setEliminandoSeleccionados] = useState(false)
  const [otModificada, setOtModificada] = useState(false)

  console.log('sedeSelect:', sedeSelect) 
  const [motivoAdicion, setMotivoAdicion] = useState('')

  // ── Modales ───────────────────────────────────────────────────────
  const [modalDuplicados, setModalDuplicados] = useState({ show: false, data: null, equiposYaAgregados: [], onSelect: null, onHide: null })
  const [modalResolverItems, setModalResolverItems] = useState({ show: false, data: null, onResolve: null, onHide: null })

  // ── Cargar OT cuando se abre el modal ─────────────────────────────
  useEffect(() => {
    if (!show || !ot?._id) return
    setLoadingOt(true)
    setPendingEquipos([])
    setMotivoAdicion('')
    setListaEquiposVisible(false)
    setFilterItem('')
    setFilterMarca('')
    setFilterSerie('')
    setFilterSede('')
    setFilterConsecutivo('')
    setFilterEstado('')
    setSortAZ(false)
    setSeleccionadosEliminar(new Set())
    setOtModificada(false)

    getDataList('Ot', { conditions: { _id: ot._id, otType: PARENT } })
      .then(res => {
        const data = res.data?.data?.[0]
        if (data) {
          setOtFull(data)
          setNumeroOrden(data.numeroOrden || '')
          setObservacion(data.observacion || '')
        }
      })
      .catch(() => Alertassw('error', 'No se pudieron cargar los datos de la OT'))
      .finally(() => setLoadingOt(false))
  }, [show, ot?._id])

  // ── Cargar sedes del cliente cuando cambia otFull ─────────────────
  useEffect(() => {
    const cid = otFull?.cliente?._id || otFull?.cliente
    if (!cid) return
    getDataList('Sedes', { conditions: { cliente: cid } })
      .then(res => setListaSedes(res.data?.data || []))
      .catch(() => {})
    // Cargar datos del cliente para pre-llenar formulario de nueva sede
    getDataList('Usuarios', { conditions: { _id: cid } })
      .then(res => setClienteData(res.data?.data?.[0] || null))
      .catch(() => {})
  }, [otFull])

  // ── Auxiliar: extraer campos de una row de equipos en OT ─────────
  const getEqInfo = (eq) => ({
    nombre:      eq.snapshot?.item      || eq.equipo?.snapshot?.item  || eq.equipo?.item?.nombre || '—',
    marca:       eq.snapshot?.marca     || eq.equipo?.snapshot?.marca || eq.equipo?.marca        || '—',
    serie:       eq.snapshot?.serial    || eq.equipo?.snapshot?.serial|| eq.equipo?.serie        || '—',
    sede:        eq.snapshot?.sede      || eq.equipo?.snapshot?.sede  || '—',
    consecutivo: eq.consecutivo         || '—',
    estado:      eq.estado              || eq.equipo?.estado          || '—',
  })

  // ── Sedes únicas presentes en los equipos de esta OT ─────────────
  const sedesEnOt = useMemo(() => {
    if (!otFull?.equipos) return []
    const set = new Set()
    otFull.equipos.forEach(eq => {
      const sede = eq.snapshot?.sede || eq.equipo?.snapshot?.sede || '—'
      if (sede && sede !== '—') set.add(sede)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [otFull?.equipos])

  // ── Filtro + orden de equipos ─────────────────────────────────────
  const equiposVisibles = useMemo(() => {
    if (!otFull?.equipos) return []
    let result = otFull.equipos.filter(eq => {
      const info = getEqInfo(eq)
      const matchItem = !filterItem.trim() || info.nombre.toLowerCase().includes(filterItem.toLowerCase())
      const matchMarca = !filterMarca.trim() || info.marca.toLowerCase().includes(filterMarca.toLowerCase())
      const matchSerie = !filterSerie.trim() || info.serie.toLowerCase().includes(filterSerie.toLowerCase())
      const matchSede = !filterSede || info.sede === filterSede
      const matchConsecutivo = !filterConsecutivo.trim() || info.consecutivo.toLowerCase().includes(filterConsecutivo.toLowerCase())
      const matchEstado = !filterEstado || info.estado === filterEstado
      return matchItem && matchMarca && matchSerie && matchSede && matchConsecutivo && matchEstado
    })
    if (sortAZ) {
      result = [...result].sort((a, b) => {
        const nA = getEqInfo(a).nombre.toLowerCase()
        const nB = getEqInfo(b).nombre.toLowerCase()
        return nA.localeCompare(nB)
      })
    }
    return result
  }, [otFull?.equipos, filterItem, filterMarca, filterSerie, filterSede, filterConsecutivo, filterEstado, sortAZ])

  // ── Guardar datos básicos ─────────────────────────────────────────
  const handleGuardarDatos = async () => {
    if (!otFull?._id) return
    setSavingDatos(true)
    try {
      await actualizarDatos('Ot', { numeroOrden, observacion }, otFull._id)
      Alertassw('ok', 'Datos guardados correctamente')
      onSaved()
    } catch {
      Alertassw('error', 'No se pudieron guardar los datos')
    } finally {
      setSavingDatos(false)
    }
  }

  // ── Selección múltiple de equipos a eliminar ──────────────────────
  const esEliminable = (info) => info.estado === 'No Finalizado'

  // Equipos que están actualmente visibles (filtros aplicados) Y son
  // eliminables. Base para el checkbox "seleccionar todos".
  const equiposEliminablesVisibles = useMemo(
    () => equiposVisibles.filter(eq => esEliminable(getEqInfo(eq))),
    [equiposVisibles]
  )

  const todosSeleccionados = equiposEliminablesVisibles.length > 0
    && equiposEliminablesVisibles.every(eq => seleccionadosEliminar.has(eq._id))

  const toggleSeleccion = (eqId) => {
    setSeleccionadosEliminar(prev => {
      const next = new Set(prev)
      if (next.has(eqId)) next.delete(eqId)
      else next.add(eqId)
      return next
    })
  }

  const toggleSeleccionTodos = () => {
    setSeleccionadosEliminar(prev => {
      const next = new Set(prev)
      if (todosSeleccionados) {
        equiposEliminablesVisibles.forEach(eq => next.delete(eq._id))
      } else {
        equiposEliminablesVisibles.forEach(eq => next.add(eq._id))
      }
      return next
    })
  }

  // Elimina en lote los equipos seleccionados; mantiene el modal abierto y
  // actualiza solo el estado local. Al cerrar el modal se dispara onSaved()
  // una sola vez si hubo cambios.
  const handleEliminarSeleccionados = async () => {
    const ids = Array.from(seleccionadosEliminar)
    if (ids.length === 0) return
    const result = await Swal.fire({
      title: `¿Eliminar ${ids.length} equipo${ids.length !== 1 ? 's' : ''}?`,
      text: 'Los equipos seleccionados serán removidos permanentemente de esta OT. Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: `Sí, eliminar ${ids.length}`,
      cancelButtonText: 'Cancelar',
    })
    if (!result.isConfirmed) return

    setEliminandoSeleccionados(true)
    let okCount = 0
    const fallidos = []
    for (const eqId of ids) {
      try {
        await removeEquipoFromOt(otFull._id, eqId)
        okCount++
        setOtFull(prev => ({
          ...prev,
          equipos: prev.equipos.filter(e => e._id !== eqId),
        }))
      } catch (err) {
        fallidos.push(eqId)
        console.error('Error eliminando equipo', eqId, err)
      }
    }
    setSeleccionadosEliminar(new Set())
    setEliminandoSeleccionados(false)
    if (okCount > 0) setOtModificada(true)
    if (fallidos.length === 0) {
      Alertassw('ok', `${okCount} equipo${okCount !== 1 ? 's' : ''} eliminado${okCount !== 1 ? 's' : ''}`)
    } else {
      Alertassw(
        'error',
        'Eliminación parcial',
        `${okCount} eliminados correctamente, ${fallidos.length} con error.`
      )
    }
  }

  // Al cerrar el modal: si hubo cambios, delega en onSaved (que cierra +
  // refresca el padre); si no, solo cierra sin recargar la lista.
  const handleCerrarModal = () => {
    if (otModificada && typeof onSaved === 'function') {
      onSaved()
    } else {
      onHide()
    }
  }

  const clienteId = otFull?.cliente?._id || otFull?.cliente

  // ── Equipos ya vinculados a la OT (para bloquear en EquipoExistente) ──
  const equiposEnOtIds = useMemo(
    () => (otFull?.equipos || []).map(e => ({ _id: e.equipo?._id || e.equipo })),
    [otFull?.equipos]
  )
  const todosEquiposAgregados = useMemo(
    () => [...pendingEquipos, ...equiposEnOtIds],
    [pendingEquipos, equiposEnOtIds]
  )

  // ── Validar duplicados (EquipoManager → EquipoManual/EquipoExcel) ─
  const validarDuplicados = async (series) => {
    if (!clienteId) return { cancelled: true, nuevosData: [], duplicadosData: [] }
    try {
      const response = await POST('/Equipos/validar-duplicados', { series, clienteId })
      const { nuevos, duplicados } = response.data
      if (duplicados.length > 0) {
        return new Promise((resolve) => {
          setModalDuplicados({
            show: true,
            data: { nuevos, duplicados },
            equiposYaAgregados: todosEquiposAgregados,
            onSelect: (selectedDuplicados) => {
              setModalDuplicados(prev => ({ ...prev, show: false }))
              resolve({ cancelled: false, nuevosData: nuevos, duplicadosData: selectedDuplicados })
            },
            onHide: () => {
              setModalDuplicados(prev => ({ ...prev, show: false }))
              resolve({ cancelled: true, nuevosData: [], duplicadosData: [] })
            },
          })
        })
      }
      return { cancelled: false, nuevosData: nuevos, duplicadosData: [] }
    } catch {
      Swal.fire('Error', 'No se pudo validar duplicados', 'error')
      return { cancelled: true, nuevosData: [], duplicadosData: [] }
    }
  }

  // ── Resolver ítems sin coincidencia (Excel) ───────────────────────
  const handleResolverItems = (equiposSinItem, callback) => {
    setModalResolverItems({
      show: true,
      data: equiposSinItem,
      onResolve: (resueltos) => {
        setModalResolverItems(prev => ({ ...prev, show: false }))
        callback(resueltos)
      },
      onHide: () => setModalResolverItems(prev => ({ ...prev, show: false })),
    })
  }

  // ── Agregar equipos pendientes ────────────────────────────────────
  const agregarEquipos = (nuevos) => {
    const arr = Array.isArray(nuevos) ? nuevos : [nuevos]
    setPendingEquipos(prev => [...prev, ...arr])
  }

  // ── Editar equipo pendiente (solo estado local) ───────────────────
  const editarEquipoPendiente = (equipoId, datos) => {
    setPendingEquipos(prev => prev.map(e => e._id === equipoId ? { ...e, ...datos } : e))
  }

  // ── Corregir equipo en BD y en estado local ───────────────────────
  const corregirEquipo = async (equipoId, datos) => {
    await actualizarDatos('Equipos', datos, equipoId)
    setPendingEquipos(prev => prev.map(e => e._id === equipoId ? { ...e, ...datos } : e))
  }

  // ── Guardar equipos pendientes en la OT ───────────────────────────
  const handleGuardarEquipos = async () => {
    if (pendingEquipos.length === 0) return
    if (!motivoAdicion) {
      Alertassw('error', 'Seleccione el motivo de adición')
      return
    }
    setSavingEquipos(true)
    try {
      const payload = {
        equipos: pendingEquipos.map(e => ({ equipo: e._id, serial: e.serie })),
        comment: 'Adición desde edición de OT',
        motivoAdicion,
      }
      await addEquipments(otFull._id, otFull.numeroOrden, PARENT, payload)
      setPendingEquipos([])
      Alertassw('ok', 'Equipos agregados a la OT')
      onSaved()
      const res = await getDataList('Ot', { conditions: { _id: otFull._id, otType: PARENT } })
      const data = res.data?.data?.[0]
      if (data) setOtFull(data)
    } catch (error) {
      if (error?.response?.data?.error?.message === 'EQUIPOS EXISTENTES') {
        const detalle = error.response.data.detail || ''
        Alertassw('error', 'Equipos ya existen en otra OT', detalle)
      } else if (error?.response?.data?.error?.message === 'DUPLICATED') {
        let msg = ''
        error.response.data.detail?.objects?.forEach(d => { msg += `• ${d.item?.nombre} - ${d.serie}\n` })
        Alertassw('toast', error.response.data.detail?.message || 'Equipo duplicado en la OT', msg, 'error', 10000)
      } else {
        Alertassw('error', 'Error agregando equipos', error?.response?.data?.error?.message || '')
      }
    } finally {
      setSavingEquipos(false)
    }
  }

      // Format options for react-dropdown-select with "Crear nueva sede..." at the top
    const options = [
        {
            value: '__crear_nueva__',
            label: '+ Crear nueva sede...',
            data: null
        },
        ...listaSedes.map(sede => ({
            value: sede._id,
            label: `${sede.nameSede || 'Sin nombre'} - ${sede.ciudad || 'Sin ciudad'}`,
            data: sede
        }))
    ];

    // react-dropdown-select llama onChange con un array
    const handleChange = (selectedOptions) => {
        const option = selectedOptions?.[0];
        if (option?.value === '__crear_nueva__') {
            setModalCrearSede({ show: true });
        } else {
            setSedeSelect(option?.data || null);
        }
    };

  const handleSubmitCrearSede = async (sedeData) => {
    try {
      const response = await crearDatos('Sedes', sedeData);
      const nuevaSede = response.data?.data || response.data;
      const cid = otFull?.cliente?._id || otFull?.cliente;
      const sedesRes = await getDataList('Sedes', { conditions: { cliente: cid } });
      setListaSedes(sedesRes.data?.data || []);
      setSedeSelect(nuevaSede);
      setModalCrearSede({ show: false });
      Alertassw('ok', 'Sede creada correctamente');
    } catch {
      Alertassw('error', 'No se pudo crear la sede');
    }
  };

  return (
    <>
      {/* Spinner global */}
      <Modalcarga estadoIn={loadingOt || savingEquipos} cambiarestadoIn={() => {}}>
        <Spinner />
      </Modalcarga>

      {/* Modal crear sede */}
      <ModalEditarSede
        show={modalCrearSede.show}
        onHide={() => setModalCrearSede({ show: false })}
        sede={null}
        clienteId={otFull?.cliente?._id || otFull?.cliente}
        onSubmit={handleSubmitCrearSede}
        loading={false}
        mode="create"
        initialData={{
          departamento: clienteData?.departamento || '',
          ciudad: clienteData?.ciudad || '',
          contSede: clienteData?.contacto || '',
          celContacto: clienteData?.telefono || '',
          emailContacto: clienteData?.correo || ''
        }}
      />

      {/* Modal duplicados */}
      <ModalDuplicados
        show={modalDuplicados.show}
        data={modalDuplicados.data || { nuevos: [], duplicados: [] }}
        equiposYaAgregados={modalDuplicados.equiposYaAgregados}
        onSelect={modalDuplicados.onSelect || (() => {})}
        onHide={modalDuplicados.onHide || (() => {})}
      />

      {/* Modal resolver ítems (Excel) */}
      <ModalResolverItems
        show={modalResolverItems.show}
        equiposSinItem={modalResolverItems.data || []}
        onResolve={modalResolverItems.onResolve || (() => {})}
        onHide={modalResolverItems.onHide || (() => {})}
      />

      {/* ── Modal principal ─────────────────────────────────────────── */}
      <Modal
        show={show}
        onHide={handleCerrarModal}
        size="lg" // quiero que ocupe más ancho en pantallas grandes
        fullscreen="md-down"
        backdrop="static"
        scrollable
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-pencil-square me-2" />
            Editar OT — {ot?.numeroOrden}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingOt ? (
            <div className="text-center py-5">
              <BSSpinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando datos de la OT...</p>
            </div>
          ) : (
            <>
              {/* ── SECCIÓN 1: Datos básicos ─────────────────────── */}
              <Card className="mb-4 border-0 shadow-sm w-100">
                <Card.Header className="bg-white fw-semibold text-secondary w-100">
                  <i className="bi bi-file-text me-2" />Datos Básicos
                </Card.Header>
                <Card.Body className="w-100">
                  <Row className="g-3">
                    <Col md={5}>
                      <Form.Label className="fw-semibold small">Número de Orden / Propuesta</Form.Label>
                      <Form.Control
                        value={numeroOrden}
                        onChange={e => setNumeroOrden(e.target.value)}
                        disabled={ot?.estado !== 'Solicitud'}
                        placeholder="Número de orden"
                      />
                      {ot?.estado !== 'Solicitud' && (
                        <Alert variant="warning" className="mt-2 py-2 small mb-0">
                          <i className="bi bi-exclamation-triangle-fill me-1" />
                          El número de propuesta solo se puede cambiar cuando la OT está en estado{' '}
                          <strong>Solicitud</strong>. Otros documentos (hojas de trabajo, actas) dependen
                          de este número.
                        </Alert>
                      )}
                    </Col>
                    <Col md={7}>
                      <Form.Label className="fw-semibold small">Observación</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={observacion}
                        onChange={e => setObservacion(e.target.value)}
                        placeholder="Observaciones de la OT..."
                      />
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end mt-3">
                    <Button variant="primary" onClick={handleGuardarDatos} disabled={savingDatos}>
                      {savingDatos
                        ? <><BSSpinner size="sm" className="me-1" />Guardando...</>
                        : <><i className="bi bi-save me-1" />Guardar datos</>}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* ── SECCIÓN 2: Equipos actuales (acordeón) ───────── */}
              <Card className="mb-4 border-0 shadow-sm w-100">
                <Card.Header
                  className="bg-white fw-semibold text-secondary w-100 d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setListaEquiposVisible(v => !v)}
                >
                  <span>
                    <i className="bi bi-tools me-2" />
                    Equipos en la OT ({otFull?.equipos?.length || 0})
                  </span>
                  <i className={`bi bi-chevron-${listaEquiposVisible ? 'up' : 'down'}`} />
                </Card.Header>

                <Collapse in={listaEquiposVisible} className='w-100'>
                  <div >
                    <Card.Body className="w-100">
                      {/* ── Filtros expandidos ── */}
                      <Row className="g-2 mb-2">
                        <Col md={4}>
                          <Form.Control
                            size="sm"
                            placeholder="Filtrar por ítem..."
                            value={filterItem}
                            onChange={e => setFilterItem(e.target.value)}
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Control
                            size="sm"
                            placeholder="Filtrar por marca..."
                            value={filterMarca}
                            onChange={e => setFilterMarca(e.target.value)}
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Control
                            size="sm"
                            placeholder="Filtrar por serie..."
                            value={filterSerie}
                            onChange={e => setFilterSerie(e.target.value)}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3 align-items-center">
                        <Col md={3}>
                          <Form.Select
                            size="sm"
                            value={filterSede}
                            onChange={e => setFilterSede(e.target.value)}
                          >
                            <option value="">Todas las sedes</option>
                            {sedesEnOt.map(s => <option key={s} value={s}>{s}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            size="sm"
                            placeholder="Filtrar por consecutivo..."
                            value={filterConsecutivo}
                            onChange={e => setFilterConsecutivo(e.target.value)}
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Select size="sm" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
                            <option value="">Todos los estados</option>
                            <option value="No Finalizado">No Finalizado</option>
                            <option value="Finalizado">Finalizado</option>
                            <option value="Aprobado">Aprobado</option>
                            <option value="Entregado">Entregado</option>
                            <option value="Cancelado">Cancelado</option>
                          </Form.Select>
                        </Col>
                        <Col md={3} className="d-flex align-items-center gap-2 flex-wrap">
                          <Form.Check
                            type="switch"
                            id="sort-az"
                            label="A–Z"
                            checked={sortAZ}
                            onChange={e => setSortAZ(e.target.checked)}
                          />
                          <Badge bg="secondary">
                            {equiposVisibles.length} resultado{equiposVisibles.length !== 1 ? 's' : ''}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              setFilterItem('')
                              setFilterMarca('')
                              setFilterSerie('')
                              setFilterSede('')
                              setFilterConsecutivo('')
                              setFilterEstado('')
                              setSortAZ(false)
                            }}
                          >
                            <i className="bi bi-x-circle me-1" />Limpiar
                          </Button>
                        </Col>
                      </Row>

                      {/* Barra de acción: eliminar en lote los seleccionados.
                          Solo se muestra cuando hay selección; evita abrir
                          el Swal con 0 items. */}
                      {seleccionadosEliminar.size > 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-2 px-2 py-1 rounded"
                             style={{ background: '#fff5f5', border: '1px solid #f1aeb5' }}>
                          <span className="small text-danger fw-semibold">
                            <i className="bi bi-check2-square me-1" />
                            {seleccionadosEliminar.size} equipo{seleccionadosEliminar.size !== 1 ? 's' : ''} seleccionado{seleccionadosEliminar.size !== 1 ? 's' : ''}
                          </span>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setSeleccionadosEliminar(new Set())}
                              disabled={eliminandoSeleccionados}
                            >
                              Limpiar selección
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={handleEliminarSeleccionados}
                              disabled={eliminandoSeleccionados}
                            >
                              {eliminandoSeleccionados
                                ? <><BSSpinner size="sm" className="me-1" />Eliminando...</>
                                : <><i className="bi bi-trash me-1" />Eliminar seleccionados</>}
                            </Button>
                          </div>
                        </div>
                      )}

                      <Table
                        hover
                        bordered
                        responsive
                        size="sm"
                        className="mb-0"
                        style={{ tableLayout: 'fixed', fontSize: '0.72rem', width: '100%' }}
                      >
                        <colgroup>
                          <col style={{ width: '34px' }} />
                          <col style={{ width: '38px' }} />
                          <col style={{ width: '22%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead className="table-dark" style={{ fontSize: '0.70rem' }}>
                          <tr>
                            <th className="text-center" title="Seleccionar todos los eliminables visibles">
                              <Form.Check
                                type="checkbox"
                                checked={todosSeleccionados}
                                onChange={toggleSeleccionTodos}
                                disabled={equiposEliminablesVisibles.length === 0 || eliminandoSeleccionados}
                                aria-label="Seleccionar todos"
                              />
                            </th>
                            <th>#</th>
                            <th>Ítem</th>
                            <th>Marca</th>
                            <th>Serie</th>
                            <th>Sede</th>
                            <th>Estado</th>
                            <th>Cons.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {equiposVisibles.map((eq, idx) => {
                            const info = getEqInfo(eq)
                            const eliminable = esEliminable(info)
                            const seleccionado = seleccionadosEliminar.has(eq._id)
                            const cellStyle = { wordBreak: 'break-word', overflowWrap: 'anywhere' }
                            return (
                              <tr key={eq._id || idx} className={seleccionado ? 'table-danger' : ''}>
                                <td className="text-center align-middle" style={cellStyle}>
                                  {eliminable ? (
                                    <Form.Check
                                      type="checkbox"
                                      checked={seleccionado}
                                      onChange={() => toggleSeleccion(eq._id)}
                                      disabled={eliminandoSeleccionados}
                                      aria-label={`Seleccionar ${info.nombre}`}
                                    />
                                  ) : (
                                    <span className="text-muted" title="Solo se pueden eliminar equipos No Finalizados">—</span>
                                  )}
                                </td>
                                <td style={cellStyle}>{idx + 1}</td>
                                <td style={cellStyle}>{info.nombre}</td>
                                <td style={cellStyle}>{info.marca}</td>
                                <td style={cellStyle}>{info.serie}</td>
                                <td style={cellStyle}>{info.sede}</td>
                                <td style={cellStyle}>
                                  <Badge bg={estadoBadge(info.estado)} style={{ fontSize: '0.65rem' }}>
                                    {info.estado}
                                  </Badge>
                                </td>
                                <td style={cellStyle}>{info.consecutivo}</td>
                              </tr>
                            )
                          })}
                          {equiposVisibles.length === 0 && (
                            <tr>
                              <td colSpan={8} className="text-center text-muted py-3">
                                {otFull?.equipos?.length === 0
                                  ? 'La OT no tiene equipos'
                                  : 'Sin resultados con los filtros aplicados'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </div>
                </Collapse>
              </Card>

              {/* ── SECCIÓN 3: Agregar equipos ───────────────────── */}
              <Card className="border-0 shadow-sm w-100">
                <Card.Header className="bg-white fw-semibold text-secondary w-100">
                  <i className="bi bi-plus-circle me-2" />Agregar Equipos
                </Card.Header>
                <Card.Body className="w-100">
                  {/* Motivo de la adición */}
                  <Row className="g-2 mb-3 align-items-end">
                    <Col md={4}>
                      <Form.Label className="fw-semibold small mb-1">
                        Motivo de adición <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value={motivoAdicion}
                        onChange={e => setMotivoAdicion(e.target.value)}
                      >
                        <option value="">— Seleccione un motivo —</option>
                        <option value="Autorizado por el cliente">Autorizado por el cliente</option>
                        <option value="Completar Cantidades">Completar Cantidades</option>
                      </Form.Select>
                    </Col>
                    <Col md={5}>
                          <Form.Label>
                              Sede *
                          </Form.Label>
                          <Select
                              size="sm"
                              options={options}
                              value={sedeSelect}
                              onChange={handleChange}
                              placeholder="Seleccione una sede..."
                              isClearable
                              styles={{
                                  option: (base, state) => ({
                                      ...base,
                                      fontWeight: state.data.value === '__crear_nueva__' ? 'bold' : 'normal',
                                      color: state.data.value === '__crear_nueva__' ? '#0d6efd' : base.color
                                  })
                              }}
                          />
                    </Col>
                    <Col md={3}>
                      {pendingEquipos.length > 0 && (
                        <div className="d-flex justify-content-end mt-3">
                          <Button variant="success" onClick={handleGuardarEquipos} disabled={savingEquipos}>
                            {savingEquipos
                              ? <><BSSpinner size="sm" className="me-1" />Guardando...</>
                              : <><i className="bi bi-cloud-upload me-1" />Guardar {pendingEquipos.length} equipo{pendingEquipos.length !== 1 ? 's' : ''}</>}
                          </Button>
                        </div>
                      )}
                    </Col>
                  </Row>

                  {pendingEquipos.length > 0 && (
                    <ListaEquiposConfirmacion
                      equipos={pendingEquipos}
                      onEliminar={(id) => setPendingEquipos(prev => prev.filter(e => e._id !== id))}
                      onEditar={editarEquipoPendiente}
                      listaSedes={listaSedes}
                      onCorregir={corregirEquipo}
                    />
                  )}

                  {clienteId && (
                    <EquipoManager
                      clienteId={clienteId}
                      sedeId={sedeSelect?._id || null}
                      onValidarDuplicados={validarDuplicados}
                      onResolverItems={handleResolverItems}
                      onAgregar={agregarEquipos}
                      equiposYaAgregados={todosEquiposAgregados}
                      listaSedes={listaSedes}
                      onCorregir={corregirEquipo}
                    />
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCerrarModal}>
            <i className="bi bi-x-circle me-1" />Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
